/**
 * 인기 검색어 API 서비스
 * WNpopword.do API를 호출하여 인기 검색어 데이터를 처리하는 서비스 모듈
 */

// API 엔드포인트
const POPULAR_SEARCH_API_URL = '/WNpopword.do';

// API 기본 파라미터
const BASE_API_PARAMS = {
    target: 'popword', // (고정값)
    range: 'day', // (고정값)
    datatype: 'json'
};

// orgType에 따른 collection 매핑
const COLLECTION_MAP = {
    'job-info': 'job',
    'foundation': 'foundation',
    'senior-info': 'portal',
    'center': 'center'
};

// 캐시 설정
const CACHE_DURATION = 5 * 60 * 1000; // 5분
const cacheStore = new Map(); // 컬렉션별 캐시 저장소

/**
 * 인기 검색어 API 서비스 클래스
 */
class PopularSearchService {

    /**
     * 인기 검색어 데이터 조회
     * @param {string} orgType - 기관 타입 (job-info, foundation, senior-info, center) 또는 searchPage 값
     * @returns {Promise<Array>} 인기 검색어 목록
     */
    static async getPopularSearches(orgType = 'job-info') {
        const collection = '_ALL_';

        try {
            if (PopularSearchService.isCacheValid(collection)) {
                return PopularSearchService.getCachedData(collection);
            }

            // API 호출
            const response = await PopularSearchService.callPopularSearchApi(collection);

            // 결과 처리 및 캐싱
            const processedData = PopularSearchService.processApiResponse(response);
            PopularSearchService.updateCache(collection, processedData);

            return processedData;

        } catch (error) {
            const cachedData = PopularSearchService.getCachedData(collection);
            return cachedData || [];
        }
    }

    /**
     * 인기 검색어 API 호출
     * @param {string} collection - 컬렉션 값
     * @returns {Promise<Object>} API 응답
     */
    static async callPopularSearchApi(collection) {
        const url = new URL(POPULAR_SEARCH_API_URL, window.location.origin);

        // API 파라미터 구성
        const apiParams = {
            ...BASE_API_PARAMS,
            collection
        };

        // URL 파라미터 설정
        Object.keys(apiParams).forEach(key => {
            url.searchParams.append(key, apiParams[key]);
        });

        // API 호출

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return await response.json();
        } else {
            throw new Error('API 응답 형식이 올바르지 않습니다.');
        }
    }

    /**
     * API 응답 데이터 처리
     * @param {Object} response - API 응답 데이터
     * @returns {Array} 가공된 인기 검색어 목록
     */
    static processApiResponse(response) {
        try {
            if (!response || !response.Data || !response.Data.Query) {
                throw new Error('Invalid API response structure');
            }

            // Query가 배열인지 단일 객체인지 확인
            let queryData;
            if (Array.isArray(response.Data.Query)) {
                queryData = response.Data.Query;
            } else if (Object.keys(response.Data.Query).length === 0) {
                // 빈 객체 {}인 경우 빈 배열로 변환
                queryData = [];
            } else {
                // 단일 객체인 경우 배열로 래핑
                queryData = [response.Data.Query];
            }

            return queryData.map(item => ({
                rank: item.id || 0,
                keyword: item.content || '',
                searchCount: item.querycount || 0,
                trend: PopularSearchService.mapTrend(item.updown),
                trendCount: Math.abs(item.count || 0),
                // 접근성을 위한 라벨
                ariaLabel: `인기 검색어 ${item.id}순위 ${item.content} 페이지로 이동`
            })).filter(item => item.keyword); // 빈 키워드 제외

        } catch (error) {
            throw new Error(`API 응답 데이터 처리 실패: ${error.message}`);
        }
    }

    /**
     * 순위 변동 상태 매핑
     * @param {string} updown - API의 updown 값 (U/D/N)
     * @returns {string} 변동 상태 (up/down/none)
     */
    static mapTrend(updown) {
        switch (updown) {
            case 'U':
                return 'up';
            case 'D':
                return 'down';
            case 'N':
            default:
                return 'none';
        }
    }

    /**
     * 캐시 유효성 검사
     * @param {string} collection - 컬렉션 값
     * @returns {boolean} 캐시가 유효한지 여부
     */
    static isCacheValid(collection) {
        const cacheEntry = cacheStore.get(collection);
        return cacheEntry &&
            cacheEntry.timestamp &&
            (Date.now() - cacheEntry.timestamp) < CACHE_DURATION;
    }

    /**
     * 캐시된 데이터 조회
     * @param {string} collection - 컬렉션 값
     * @returns {Array|null} 캐시된 데이터
     */
    static getCachedData(collection) {
        const cacheEntry = cacheStore.get(collection);
        return cacheEntry ? cacheEntry.data : null;
    }

    /**
     * 캐시 업데이트
     * @param {string} collection - 컬렉션 값
     * @param {Array} data - 캐시할 데이터
     */
    static updateCache(collection, data) {
        cacheStore.set(collection, {
            data,
            timestamp: Date.now()
        });
    }

    /**
     * orgType에 따른 collection 매핑
     * @param {string} orgType - 기관 타입 또는 searchPage 값
     * @returns {string} 매핑된 collection 값
     */
    static getCollectionFromOrgType(orgType) {
        // URL의 searchPage 파라미터 확인
        const urlParams = new URLSearchParams(window.location.search);
        const searchPage = urlParams.get('searchPage');

        // searchPage가 있으면 해당 값을 우선 사용
        const targetOrgType = searchPage || orgType;

        // COLLECTION_MAP을 사용하여 매핑
        return COLLECTION_MAP[targetOrgType] || 'job';
    }
}

export default PopularSearchService;
export {PopularSearchService};


// WEBPACK FOOTER //
// ./src/main/search/services/popularSearchService.js